import * as React from "react";
import { createContext, useState } from "react";

export interface Member {
  displayName: string;
  userPrincipalName: string;
  jobTitle: string;
  id: string;
}

export interface Group {
  id: string;
  displayName: string;
  description: string;
  createdDateTime: string;
}

interface AdContextProps {
  email: string;
  section: string;
  setSection: (sr: string) => void;
  resources: number;
  setResources: (sr: number) => void;
  group?: Group;
  setGroup: React.Dispatch<React.SetStateAction<Group | undefined>>;
  selectedRows: Member[];
  setSelectedRows: React.Dispatch<React.SetStateAction<Member[]>>;
  deleteModal: boolean;
  setDeleteModal: React.Dispatch<React.SetStateAction<boolean>>;
  addModal: boolean;
  setAddModal: React.Dispatch<React.SetStateAction<boolean>>;
  removeToast: boolean;
  setRemoveToast: React.Dispatch<React.SetStateAction<boolean>>;
  addToast: boolean;
  setAddToast: React.Dispatch<React.SetStateAction<boolean>>;
  refreshTableFlag: boolean;
  setRefreshTableFlag: React.Dispatch<React.SetStateAction<boolean>>;
  navigateToGroup: (group: Group) => void;
  navigateToDashboard: () => void;
}

const AdContext = createContext<AdContextProps>({
  email: "",
  section: "table",
  setSection: () => {},
  resources: 0,
  setResources: () => {},
  group: undefined,
  setGroup: () => {},
  selectedRows: [],
  setSelectedRows: () => {},
  deleteModal: false,
  setDeleteModal: () => {},
  addModal: false,
  setAddModal: () => {},
  removeToast: false,
  setRemoveToast: () => {},
  addToast: false,
  setAddToast: () => {},
  refreshTableFlag: false,
  setRefreshTableFlag: () => {},
  navigateToGroup: () => {},
  navigateToDashboard: () => {},
});

interface AdProviderProps {
  children: React.ReactNode;
  email: string;
}

const AdProvider: React.FC<AdProviderProps> = ({ children, email }) => {
  const [section, setSection] = useState<string>("table");
  const [resources, setResources] = useState<number>(0);
  const [selectedRows, setSelectedRows] = React.useState<Member[]>([]);
  const [deleteModal, setDeleteModal] = React.useState<boolean>(false);
  const [addModal, setAddModal] = React.useState<boolean>(false);
  const [removeToast, setRemoveToast] = React.useState(false);
  const [addToast, setAddToast] = React.useState(false);
  const [group, setGroup] = React.useState<Group>();
  const [refreshTableFlag, setRefreshTableFlag] = React.useState(false);

  const updateURL = (groupId?: string) => {
    const url = new URL(window.location.href);
    if (groupId) {
      url.searchParams.set("groupId", groupId);
      url.searchParams.set("section", "table");
    } else {
      url.searchParams.delete("groupId");
      url.searchParams.set("section", "dashboard");
    }
    window.history.pushState({}, "", url.toString());
  };

  const navigateToGroup = React.useCallback((selectedGroup: Group) => {
    sessionStorage.setItem("activeGroup", JSON.stringify(selectedGroup));
    setGroup(selectedGroup);
    setSection("table");
    updateURL(selectedGroup.id);
  }, []);

  const navigateToDashboard = React.useCallback(() => {
    sessionStorage.removeItem("activeGroup");
    setSection("dashboard");
    updateURL();
  }, []);

  // Restore section and group from sessionStorage/URL on page reload
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("groupId") && params.get("section") === "table") {
      const saved = sessionStorage.getItem("activeGroup");
      if (saved) {
        try {
          setGroup(JSON.parse(saved));
        } catch {
          sessionStorage.removeItem("activeGroup");
        }
      }
      setSection("table");
    }
  }, []);

  // Sync section with browser back/forward
  React.useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      if (params.get("groupId") && params.get("section") === "table") {
        setSection("table");
      } else {
        setSection("table");
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  return (
    <AdContext.Provider
      value={{
        email,
        section,
        setSection,
        resources,
        setResources,
        group,
        setGroup,
        selectedRows,
        setSelectedRows,
        deleteModal,
        setDeleteModal,
        addModal,
        setAddModal,
        addToast,
        setAddToast,
        removeToast,
        setRemoveToast,
        refreshTableFlag,
        setRefreshTableFlag,
        navigateToGroup,
        navigateToDashboard,
      }}
    >
      {children}
    </AdContext.Provider>
  );
};

export { AdContext, AdProvider };
